import React from 'react';
import { Image } from 'antd';
import {
  FilePdfFilled,
  FilePptFilled,
  FileTextFilled,
  VideoCameraFilled,
  LayoutFilled,
} from '@ant-design/icons';
import { PanelsTopLeft, Rss } from 'lucide-react';
import _ from 'lodash';
import './index.scss';

import NatureLogoUrl from '../../assets/images/1-about/nature.jpg';
import ScienceLogoTransparentUrl from '../../assets/images/1-about/science-transparent.png';
import ChemistryWorldLogoUrl from '../../assets/images/8-news/chemistry-world-logo.png';
import award from '../HomePage/ResearchAndNewsSection/award.png';
import newIconUrl from '../../assets/images/2-research/icon/new.svg';

// One publication, as on the homepage: teaser, title, authors, venue (the short venue as a black tag), award and the
// [ PDF | ... ] links. Used by the homepage's Selected Research and the WHALES Lab publication list, so both look the same.

// "NCS2026" -> "NCS 2026"
const venueTag = (s: string) => s.replace(/([A-Za-z)])(\d{4})/, '$1 $2');

const suppIcon = (name: string) => {
  if (name.includes('PDF') || name === 'Research Briefing') return <FilePdfFilled />;
  if (name === 'Demo Video' || name === 'Background Video' || name === 'Case Video' || name.includes('Talk')) return <VideoCameraFilled />;
  if (name.includes('Slides')) return <FilePptFilled />;
  if (name === 'Project Page' || name === 'Official Website') return <LayoutFilled />;
  if (name === 'Supp Material' || name.includes('Poster') || name.includes('Journal Cover')) return <FileTextFilled />;
  if (name === 'Online System' || name.includes('Code Repository')) return <PanelsTopLeft size={16} fill='currentColor' stroke='white' />;
  if (name === 'Media Coverage') return <Rss size={13} strokeWidth={3} />;
  return null;
};

const mediaLogo = (name: string) =>
  name === 'Nature' ? NatureLogoUrl : name === 'Science' ? ScienceLogoTransparentUrl : name === 'Chemistry World' ? ChemistryWorldLogoUrl : '';

const PaperItem: React.FC<{ paper: any }> = ({ paper }) => {
  const supp = (paper.suppMaterialList || []) as { name: string, fileLink: any }[];
  // the full venue is left out when it only repeats the tag (e.g. "arXiv 2026" + "arXiv")
  const tagName = String(paper.venueShort || '').replace(/\s*\d{4}.*$/, '').trim().toLowerCase();
  const showVenue = !!paper.venue && String(paper.venue).trim().toLowerCase() !== tagName;
  return (
    <div className='paper-item'>
      <div className='pi-teaser'>
        {/* teaserCrop: fill a 16:10 frame (as on the WHALES Lab page) instead of showing the whole image */}
        <div className={`pi-img${paper.teaserCrop ? ' is-crop' : ''}`}>
          <Image src={paper.teaser} alt={paper.venueShort} placeholder={<Image preview={false} src={paper.teaser} />} />
        </div>
      </div>
      <div className='pi-info'>
        <div className='pi-title'>
          {paper.isNew ? (
            // the NEW badge hangs after the title, held to its last word so it never drops onto a line of its own
            <>
              {paper.title.slice(0, paper.title.lastIndexOf(' ') + 1)}
              <span className='pi-nowrap'>{paper.title.slice(paper.title.lastIndexOf(' ') + 1)}<img className='pi-new' src={newIconUrl} alt='new' /></span>
            </>
          ) : paper.title}
        </div>
        <div className='pi-author'>
          {(paper.authorList || []).map((a: string, i: number) => (
            <React.Fragment key={i}>
              {/^Yifang Wang/.test(a) ? <span className='pi-me'>{a}</span> : <span>{a}</span>}
              {i < paper.authorList.length - 1 && ', '}
            </React.Fragment>
          ))}
        </div>
        <div className='pi-venue'>
          <span className='pi-tag'>{venueTag(paper.venueShort)}</span>
          {showVenue && <span className='pi-venue-long'>{paper.venue}</span>}
        </div>
        {paper.award && (
          <div className='pi-award'>
            <span className='pi-award-icon'><img src={award} alt='' /></span>
            <span>{paper.award}</span>
          </div>
        )}
        {supp.length > 0 && (
          <div className='pi-supp'>
            [{supp.map((s, i) => (
              <span className='pi-supp-unit' key={i}>
                {suppIcon(s.name)}
                {s.name === 'Media Coverage' && typeof s.fileLink === 'object' ? (
                  <>
                    <span className='pi-supp-name'>{s.name}</span>:
                    {_.map(s.fileLink, (link: string, media: string) => (
                      <a key={media} href={link} className='pi-media'><img src={mediaLogo(media)} alt={media} /></a>
                    ))}
                  </>
                ) : (
                  <a href={s.fileLink}>{s.name}</a>
                )}
                {i < supp.length - 1 && <span className='pi-sep'>|</span>}
              </span>
            ))}&nbsp;]
          </div>
        )}
      </div>
    </div>
  );
};

export default PaperItem;
